import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { User, Shield, Home, CreditCard, MessageSquareHeartIcon, BookOpen, RotateCcw, PenLine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useDueQuestions } from '@/hooks/useQuizPractice';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ui/theme-toggle';

function PracticeButton({ mobile }: { mobile?: boolean }) {
  const { data: dueData } = useDueQuestions();
  const dueCount = dueData?.questions?.length ?? 0;

  if (dueCount === 0) return null;

  if (mobile) {
    return (
      <Link to="/practice">
        <Button variant="default" size="sm" className="relative px-2 h-9">
          <RotateCcw className="h-4 w-4" />
          <Badge variant="secondary" className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 text-[10px] leading-none flex items-center justify-center rounded-full">
            {dueCount}
          </Badge>
        </Button>
      </Link>
    );
  }

  return (
    <Link to="/practice">
      <Button variant="default" size="sm" className="gap-1.5">
        <RotateCcw className="h-4 w-4" />
        Practise Now
        <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1.5 text-xs rounded-full">
          {dueCount}
        </Badge>
      </Button>
    </Link>
  );
}

export const Navbar: React.FC = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [profileImageError, setProfileImageError] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const navigationItems = [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    ...(isAdmin ? [{ to: '/admin', label: 'Admin', icon: Shield }] : [])
  ];

  return (
    <nav className="bg-background shadow-sm border-b">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {/* App Logo Header */}
            <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex-shrink-0 flex items-center">
              <span className="text-xl font-bold text-primary">MemoriseIt</span>
            </Link>
          </div>

          <div className="flex items-center">
            {isAuthenticated ? (
              <>
                {/* Practice CTA - Desktop */}
                <div className="hidden sm:flex items-center mr-2">
                  <PracticeButton />
                </div>

                {/* Regular Header on all */}
                <div className="hidden sm:flex items-center space-x-2 mr-4">
                  {navigationItems.map((item) => (
                    <Link key={item.to} to={item.to}>
                      <Button variant="ghost" size="sm">
                        {item.icon && <item.icon className="h-4 w-4" />}
                        {item.label}
                      </Button>
                    </Link>
                  ))}
                  <ThemeToggle />
                </div>

                {/* Header and Navbar on Mobile (Icons only) */}
                <div className="sm:hidden flex items-center space-x-1 mr-2">
                  <PracticeButton mobile />
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link key={item.to} to={item.to}>
                        <Button variant="ghost" className="p-2">
                          {Icon && <Icon className="h-5 w-5" />}
                        </Button>
                      </Link>
                    );
                  })}
                  <ThemeToggle />
                </div>
                
                {/* Profile Circle and Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full ml-2" data-testid="profile-button">
                      <Avatar className="h-10 w-10">
                        {!profileImageError && user?.user_metadata?.avatar_url && (
                          <AvatarImage 
                            src={user.user_metadata.avatar_url} 
                            alt={user.user_metadata?.full_name || user?.email || "User"}
                            onError={() => setProfileImageError(true)}
                          />
                        )}
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {user?.user_metadata?.full_name 
                            ? getInitials(user.user_metadata.full_name)
                            : user?.email?.[0].toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {user?.user_metadata?.full_name || 'User'}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user?.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {/* Profile link hidden for now
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="w-full cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        <span>Profile</span>
                      </Link>
                    </DropdownMenuItem>
                    */}
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={async () => {
                        try {
                          const { data, error } = await (await import('@/integrations/supabase/client')).supabase.functions.invoke('customer-portal');
                          if (error) throw error;
                          if (data?.url) window.location.href = data.url;
                        } catch (err) {
                          console.error('Billing portal error:', err);
                        }
                      }}
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      <span>Billing</span>
                    </DropdownMenuItem>
                    
                    {/* Feedback Button */}
                    <DropdownMenuItem asChild>
                      <Link to="/feedback" className="w-full cursor-pointer">
                        <MessageSquareHeartIcon className="mr-2 h-4 w-4" />
                        <span>Feedback</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {/* Sign Out Button */}
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {/* Desktop: full buttons with text */}
                <div className="hidden sm:flex items-center space-x-2">
{/*                   <Link to="/docs">
                    <Button variant="ghost" size="sm">
                      <BookOpen className="h-4 w-4" />
                      Docs
                    </Button>
                  </Link> */}
                  <Link to="/blog">
                    <Button variant="ghost" size="sm">
                      <PenLine className="h-4 w-4" />
                      Blog
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="ghost" size="sm">
                      <CreditCard className="h-4 w-4" />
                      Pricing
                    </Button>
                  </Link>
                  <ThemeToggle />
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button variant="default" size="sm">
                      Sign Up
                    </Button>
                  </Link>
                </div>
                {/* Mobile: icon-only buttons if not authentificated */}
                <div className="flex sm:hidden items-center space-x-1">
{/*                   <Link to="/docs">
                    <Button variant="ghost" size="icon" className="h-9 w-9">
                      <BookOpen className="h-5 w-5" />
                    </Button>
                  </Link> */}
                  <Link to="/blog">
                    <Button variant="ghost" size="icon" className="h-9 w-9">
                      <PenLine className="h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="ghost" size="icon" className="h-9 w-9">
                      <CreditCard className="h-5 w-5" />
                    </Button>
                  </Link>
                  <ThemeToggle />
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="px-2">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button variant="default" size="sm" className="px-2">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
